import React, { useState } from 'react';
import { useCurrentUser } from 'javascripts/hooks';
import { useMutation } from '@apollo/client';

import { userHasPermission, MOVE_PHOTOS } from 'helpers/permissions';
import eventEmitter from 'helpers/event-emitter';
import { ASSETS_REASSIGNED } from 'constants/graphqlEvents';
import ReassignPhotosModal from 'components/photos/ReassignPhotosModal';
import MUTATION_MOVE_ASSETS from './gql/mutateMoveAssets.graphql';

const NO_ACTION = null;
const NO_MODAL = () => {};
const EMPTY_RESULT = [NO_ACTION, NO_MODAL];

export default function useMenuActionMove({ assets, onSuccess }) {
  const [modalVisible, setModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const user = useCurrentUser();

  const [moveAssets] = useMutation(MUTATION_MOVE_ASSETS, {
    onCompleted: (data) => {
      const { assets: assetItems, errors } = data.moveAssets;
      if (errors.length) {
        setErrorMessage(
          `There was an error moving photos: ${[
            ...new Set(errors.map((e) => e.message)),
          ].join(', ')}`,
        );
      } else {
        eventEmitter.trigger(ASSETS_REASSIGNED, {
          assetItems,
        });

        setModalVisible(false);
        onSuccess();
      }
    },
    onError: (error) => {
      setErrorMessage(`Unable to move photos: ${error.message}`);
    },
  });

  const hasPermission = userHasPermission(user, MOVE_PHOTOS);
  const validSelection = assets.length > 0;

  if (!hasPermission || !validSelection) {
    return EMPTY_RESULT;
  }

  const action = () => {
    setModalVisible(true);
  };

  const renderModal = () => {
    if (!modalVisible) {
      return false;
    }

    const assetIds = assets.map((asset) => asset.id);

    const onCancel = () => {
      setModalVisible(false);
    };

    const onReassignClicked = async ({ projectNodeId }) => {
      if (!projectNodeId) {
        return;
      }
      setErrorMessage(null);
      moveAssets({
        variables: { assetIds, destinationId: projectNodeId },
      });
    };

    return (
      <ReassignPhotosModal
        isOpen={modalVisible}
        errorMessage={errorMessage}
        onRequestClose={onCancel}
        onReassignClicked={onReassignClicked}
        companyId={user.company_id}
      />
    );
  };

  return [action, renderModal];
}
